import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { RatingLevelTranslations } from '@recruitee/interview-results-types';
import { RatingLevel } from '@recruitee/interview-results-types';

import { IconName } from '../../icons-container/icons.type';
import { IconColorDirective } from '../icon/directives/icon-color.directive';
import { IconComponent, IconSizes } from '../icon/icon.component';
import { RtTooltip } from '../tooltip/tooltip.directive';
import { TruncateComponent } from '../truncate/truncate.component';
import { ComponentVariant, Variants } from '../variant/variant';

const DEFAULT_RATING_LEVEL_TRANSLATIONS: RatingLevelTranslations = {
  [RatingLevel.StrongYes]: 'rtu.interview.summary.strong_yes',
  [RatingLevel.Yes]: 'rtu.interview.summary.yes',
  [RatingLevel.Neutral]: 'rtu.interview.summary.neutral',
  [RatingLevel.No]: 'rtu.interview.summary.no',
};

@Component({
  selector: 'rt-interview-evaluation-rating',
  templateUrl: 'interview-evaluation-rating.component.html',
  styleUrls: ['./interview-evaluation-rating.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': 'variant',
  },
  imports: [RtTooltip, IconColorDirective, IconComponent, TruncateComponent, TranslatePipe],
})
export class InterviewEvaluationRatingComponent {
  public static readonly VARIANTS: Variants = [
    'default',
    ['default', 'hasLabel', 'other', 'no-padding'],
  ];

  public readonly iconMap: { [key: string]: IconName } = {
    [RatingLevel.StrongYes]: 'rating-double-thumb-up',
    [RatingLevel.Yes]: 'rating-thumb-up',
    [RatingLevel.Neutral]: 'rating-thumbs-up-down',
    [RatingLevel.No]: 'rating-thumb-down',
  };

  @Input() public translations?: RatingLevelTranslations;

  @ComponentVariant(InterviewEvaluationRatingComponent.VARIANTS)
  @Input()
  public variant: string;

  @Input()
  public set rating(value: number | string | null) {
    if (typeof value === 'number') {
      this.ratingKey = this.getRatingName(value);
    } else {
      this.ratingKey = this.mapStringToRating(<RatingLevel>value);
    }
  }

  @Input() public stage: any;
  @Input() public size: keyof typeof IconSizes = 'medium';
  @Input() public centered: boolean = true;
  @Input() public tooltip: boolean = true;
  @Input() public colorize: boolean = true;
  @Input() public offer?: { title: string };

  public ratingKey: RatingLevel;

  constructor(private i18n: I18nService) {}

  public isGreen(rating: RatingLevel): boolean | null {
    return this.hasRating([RatingLevel.StrongYes, RatingLevel.Yes], rating);
  }

  public isGrey(rating: RatingLevel): boolean | null {
    return this.hasRating([RatingLevel.No, RatingLevel.Neutral], rating);
  }

  public hasVariant(variant: string): boolean {
    return (this.variant || '').includes(variant);
  }

  public getRatingTranslation(ratingKey: RatingLevel): string {
    const translations = this.translations || DEFAULT_RATING_LEVEL_TRANSLATIONS;
    return translations[ratingKey] ? this.i18n.translate(translations[ratingKey]) : '-';
  }

  private hasRating(ratings: RatingLevel[], rating: RatingLevel): true | null {
    return ratings.includes(rating) || null;
  }

  private getRatingName(rating: number): RatingLevel {
    if (rating <= 25) {
      return RatingLevel.No;
    } else if (rating <= 50) {
      return RatingLevel.Neutral;
    } else if (rating <= 75) {
      return RatingLevel.Yes;
    } else {
      return RatingLevel.StrongYes;
    }
  }

  private mapStringToRating(name: RatingLevel): RatingLevel {
    const ratingMap = {
      strong_yes: RatingLevel.StrongYes,
      yes: RatingLevel.Yes,
      neutral: RatingLevel.Neutral,
      no: RatingLevel.No,
    };

    return ratingMap[name];
  }
}
